"use client";
import { useEffect, useRef } from "react";
import { createClient } from "@/lib/supabase/browser";

type Props = { minutes?: number; remember?: boolean; maxSessionHours?: number };

export default function InactivityGuard({ 
  minutes = 90, 
  remember = false,
  maxSessionHours = 24 // Maximum session duration in hours (default 24 hours)
}: Props) {
  const supabase = createClient();
  const inactivityTimer = useRef<NodeJS.Timeout | null>(null);
  const sessionTimer = useRef<NodeJS.Timeout | null>(null);
  const sessionStartTime = useRef<number | null>(null);
  const inactivityTimeoutMs = minutes * 60 * 1000; // Convert minutes to milliseconds
  const maxSessionMs = maxSessionHours * 60 * 60 * 1000; // Convert hours to milliseconds

  // Check session age and sign out if exceeded
  const checkSessionAge = async () => {
    if (!sessionStartTime.current) {
      // Get session start time from the session itself
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.expires_at) {
        // Calculate when session was created (expires_at - 1 hour for access token, but we'll use it as reference)
        // Actually, we'll track when the component mounted as session start
        sessionStartTime.current = Date.now();
      }
      return;
    }

    const sessionAge = Date.now() - sessionStartTime.current;
    if (sessionAge > maxSessionMs) {
      // Session has exceeded maximum duration, sign out
      try {
        await supabase.auth.signOut();
        window.location.assign("/login?reason=session_expired");
      } catch (error) {
        console.error('Error signing out after session expiry:', error);
      }
    }
  };

  // Reset inactivity timer
  function resetInactivityTimer() {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(async () => {
      try {
        await supabase.auth.signOut();        // revoke local session
      } finally {
        window.location.assign("/login?reason=timeout");
      }
    }, inactivityTimeoutMs);
  }

  useEffect(() => {
    // Initialize session start time (async function inside useEffect)
    const initializeSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        sessionStartTime.current = Date.now();
      }
    };
    
    initializeSession();

    // Set up inactivity monitoring
    resetInactivityTimer();
    const events = ["mousemove", "keydown", "scroll", "click", "touchstart", "visibilitychange"];
    const handler = () => { 
      if (!document.hidden) {
        resetInactivityTimer();
      }
    };
    events.forEach(ev => window.addEventListener(ev, handler, { passive: true }));

    // Set up session age check (check every hour)
    const sessionCheckInterval = setInterval(checkSessionAge, 60 * 60 * 1000); // Check every hour

    return () => {
      events.forEach(ev => window.removeEventListener(ev, handler));
      if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
      if (sessionTimer.current) clearTimeout(sessionTimer.current);
      clearInterval(sessionCheckInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inactivityTimeoutMs, maxSessionMs]);

  return null;
}
